import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, shareReplay, take, tap } from 'rxjs/operators';

import { AssetAccountantSubscription } from '@assetaccountant/api/models/asset-accountant-subscription';
import { Feature } from '@assetaccountant/api/models/feature';
import { SubscriptionPackageOptions } from '@assetaccountant/api/models/subscription-package-options';
import { SubscriptionStatus } from '@assetaccountant/api/models/subscription-status';
import { OrganisationV2Api } from '@assetaccountant/api/services/organisation-v2.api';
import { RegisterV3Api } from '@assetaccountant/api/services/register-v3.api';
import { MAXIMUM_DEMO_ASSETS } from '@assetaccountant/assets/services/assets.service';
import { environment } from 'environments/environment';
import { Register } from '../models/register';
import { AppNavigationService } from './app-navigation.service';
import { LayoutService } from './layout.service';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  private lastUpdateTimestamp?: number;
  private orgSubscription$: Observable<AssetAccountantSubscription> | null;
  private orgSubscriptionId: number | null;

  isSubscriptionSuspended = false;

  subscription: BehaviorSubject<AssetAccountantSubscription> = new BehaviorSubject(
    {} as AssetAccountantSubscription,
  );

  subscriptionPackageOptions?: SubscriptionPackageOptions;

  constructor(
    private appNavigationService: AppNavigationService,
    private organisationApi: OrganisationV2Api,
    private layoutService: LayoutService,
    private registerApi: RegisterV3Api,
  ) {
    this.appNavigationService.onOrganisationChanged.subscribe(() => {
      this.reset();
    });
  }

  /**
   * Check if the user can add assets
   *
   * @param organisationId specify the organisation
   * @param register optionally include the register so we can check if it is a demo
   */
  canAddAssets(organisationId: number, register: Register | null = null): Observable<boolean> {
    if (register?.isDemo) {
      return this.registerApi.getAssetCount().pipe(
        map(({ count }) => {
          return count <= MAXIMUM_DEMO_ASSETS;
        }),
      );
    } else {
      return this.getSubscription(organisationId).pipe(
        map(subscription => subscription.canAddAssets),
      );
    }
  }

  /**
   * Gets the subscription in state, or else makes API request
   *
   * @param organisationId specify the organisation
   */
  getSubscription(
    organisationId?: number,
    updatePackageOptions = true,
  ): Observable<AssetAccountantSubscription> {
    if (!this.orgSubscription$ || (organisationId && this.orgSubscriptionId !== organisationId)) {
      this.orgSubscription$ = this.organisationApi.getSubscription(organisationId).pipe(
        tap({
          error: () => {
            this.orgSubscription$ = null;
          },
        }),
        map(response => {
          this.lastUpdateTimestamp = Date.now();
          this.subscription.next(response.subscriptionStatus);
          if (updatePackageOptions) {
            this.fetchPackageOptions(organisationId);
          }

          this.isSubscriptionSuspended =
            response.subscriptionStatus.status === SubscriptionStatus.Suspended;
          this.layoutService.setPaymentRequired(this.isSubscriptionSuspended);

          return response.subscriptionStatus;
        }),
        shareReplay(1),
      );
    }

    if (organisationId) {
      this.orgSubscriptionId = organisationId;
    }
    return this.orgSubscription$;
  }

  /**
   * Clear the subscription in state
   */
  reset() {
    this.orgSubscription$ = null;
    this.orgSubscriptionId = null;
  }

  isFeatureEnabled(organisationId: number, feature: Feature | undefined): Observable<boolean> {
    if (!organisationId || isNaN(organisationId)) {
      return of(false);
    }

    // Enable if no feature is specified
    if (!feature) {
      return of(true);
    }

    return this.getSubscription(organisationId).pipe(
      map(subscription => {
        return subscription?.features.includes(feature);
      }),
    );
  }

  areFeaturesAvailableInPlans(usedFeatures: Feature[]): boolean {
    if (this.subscriptionPackageOptions) {
      const planFeatures = [
        ...new Set(this.subscriptionPackageOptions.options.flatMap(option => option.features)),
      ];
      return usedFeatures.filter(f => !planFeatures.includes(f)).length === 0;
    } else if (!environment.production) {
      console.error('SubscriptionPackageOptions not loaded');
    }
    return false;
  }

  /**
   * Checks the age of the cached subscription data and refreshes if older than one day
   */
  checkSubscriptionIsCurrent() {
    if (this.lastUpdateTimestamp && Date.now() - this.lastUpdateTimestamp > 24 * 60 * 60_000) {
      this.lastUpdateTimestamp = Date.now();
      this.orgSubscription$ = null;
      this.getSubscription(undefined, false).pipe(take(1)).subscribe();
    }
  }

  private fetchPackageOptions(organisationId?: number) {
    this.organisationApi
      .getSubscriptionPackageOptions({ shallowCheck: true, organisationId })
      .pipe(take(1))
      .subscribe(({ upgradeOptions }) => (this.subscriptionPackageOptions = upgradeOptions));
  }
}
